/* eslint-disable no-negated-condition */
import React from 'react';
import SVG from 'react-inlinesvg';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Checkbox, Divider, Tooltip, useTheme } from '@mui/material';
import styled, { css } from 'styled-components';

import CircleSVG from '@/assets/icons/circle.svg';
import CircleEmptySVG from '@/assets/icons/circle-empty.svg';
import CircleMarkedSVG from '@/assets/icons/circle-marked.svg';
import SquareSVG from '@/assets/icons/square.svg';
import SquareEmptySVG from '@/assets/icons/square-empty.svg';
import SquareMarkedSVG from '@/assets/icons/square-marked.svg';
import TriangleSVG from '@/assets/icons/triangle.svg';
import TriangleEmptySVG from '@/assets/icons/triangle-empty.svg';
import TriangleMarkedSVG from '@/assets/icons/triangle-marked.svg';
import { CustomSwitch } from '@/features/canvas/components/CustomSwitch';
import { DEFAULT_NODE_RADIUS } from '@/features/canvas/constants';
import { CHARACTER_LIMITS } from '@/features/canvas/constants/character-limits';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { getDisplayText } from '@/features/canvas/utils/text-utils';
import { components } from '@/types/api';

const FilterSidebarItem = ({
  filter,
  optionsOverride,
}: {
  filter: components['schemas']['Filter'];
  optionsOverride?: components['schemas']['FilterOption'][];
}) => {
  const theme = useTheme();
  const {
    filterNodes,
    filteredNodes,
    setFilteredNodes,
    filters,
    activeFilterIds,
    setActiveFilterIds,
    optionCheckedIds,
    setOptionCheckedIds,
    setShowDependencies,
    isMultiFilterEnabled,
  } = useEditorContext();

  type FilterOptionWithSort = components['schemas']['FilterOption'] & {
    sortOrder?: number;
    sort_order?: number;
  };

  // Handle both sortOrder (from API) and sort_order (legacy/type definition)
  const options = optionsOverride
    ? [...optionsOverride].sort((a, b) => {
        const aSortOrder =
          (a as FilterOptionWithSort).sortOrder ?? (a as FilterOptionWithSort).sort_order ?? 0;
        const bSortOrder =
          (b as FilterOptionWithSort).sortOrder ?? (b as FilterOptionWithSort).sort_order ?? 0;
        return aSortOrder - bSortOrder;
      })
    : [...(filter.options as FilterOptionWithSort[])].sort((a, b) => {
        const aSortOrder = a.sortOrder ?? a.sort_order ?? 0;
        const bSortOrder = b.sortOrder ?? b.sort_order ?? 0;
        return aSortOrder - bSortOrder;
      });

  const getFilterIcon = (color: string, isChecked: boolean, selection: 'single' | 'multiple') => {
    if (isMultiFilterEnabled) {
      return (
        <StyledCheckbox
          checked={isChecked}
          icon={<CheckBoxOutlineBlankIcon />}
          checkedIcon={<CheckBoxIcon />}
        />
      );
    }

    let fillColor = color;
    let addIcon = isChecked ? CircleEmptySVG : CircleSVG;

    if (selection === 'single') {
      if (filter.shape === 'triangle') {
        addIcon = isChecked ? TriangleEmptySVG : TriangleSVG;
      } else if (filter.shape === 'square') {
        addIcon = isChecked ? SquareEmptySVG : SquareSVG;
      }
    }

    if (selection === 'multiple') {
      fillColor = theme.palette.brand.textSecondary;
      addIcon = isChecked ? CircleMarkedSVG : CircleEmptySVG;
      if (filter.shape === 'triangle') {
        addIcon = isChecked ? TriangleMarkedSVG : TriangleEmptySVG;
      } else if (filter.shape === 'square') {
        addIcon = isChecked ? SquareMarkedSVG : SquareEmptySVG;
      }
    }

    return (
      <StyledSVG
        src={addIcon}
        $color={color}
        $isSquare={filter.shape === 'square'}
        $isChecked={isChecked}
        $fillColor={fillColor}
        $isMultiFilter={selection === 'multiple'}
      />
    );
  };

  const updateFilteredNodesMultiFilter = (remainingFilters: number[]) => {
    setFilteredNodes(prevNodes =>
      prevNodes.map(node => {
        const isFilteredByAny = remainingFilters.some(fId =>
          filters.find(f => f.id === fId)?.nodes?.some(n => n.nodeId === node.id)
        );
        return {
          ...node,
          shape: 'circle',
          color: isFilteredByAny ? theme.palette.brand.editorTabPrimary : '',
          size: DEFAULT_NODE_RADIUS,
        };
      })
    );
  };

  const updateFilteredNodesSingleFilter = (selectedFilter: components['schemas']['Filter']) => {
    const updatedNodes = filteredNodes.map(node => {
      const filteredNode = selectedFilter.nodes?.find(n => n.nodeId === node.id);
      const optionColor =
        selectedFilter.options.find(opt => opt.id === filteredNode?.optionId)?.color || '';
      const optionSize = selectedFilter.options.find(
        opt => opt.id === filteredNode?.optionId
      )?.size;

      return {
        ...node,
        shape: filteredNode ? selectedFilter.shape : 'circle',
        color: filteredNode ? optionColor : '',
        size: filteredNode ? optionSize : node.size,
      };
    });

    setFilteredNodes(updatedNodes);
  };

  const handleChecked = (option: components['schemas']['FilterOption']) => {
    if (optionCheckedIds.includes(option.id!)) {
      const newOptionCheckedIds = optionCheckedIds.filter(id => id !== option.id!);
      setOptionCheckedIds(newOptionCheckedIds);

      const remainingFilters = filters
        .filter(f => f.options.some(o => newOptionCheckedIds.includes(o.id!)))
        .map(f => f.id!);

      setActiveFilterIds(remainingFilters);
      updateFilteredNodesMultiFilter(remainingFilters);
      return;
    }

    setShowDependencies(false);
    setOptionCheckedIds(prev => [...prev, option.id!]);

    filterNodes({
      filter,
      optionId: option.id!,
      optionColor: option.color,
      optionSize: option.size ?? DEFAULT_NODE_RADIUS,
      multiFilterColor: theme.palette.brand.textSecondary,
    });

    if (isMultiFilterEnabled) {
      setActiveFilterIds(prev => {
        const updatedIds = prev.includes(filter.id!) ? prev : [...prev, filter.id!];

        updateFilteredNodesMultiFilter(updatedIds);
        return updatedIds;
      });
    } else {
      setOptionCheckedIds([option.id!]);
      setActiveFilterIds([filter.id!]);
      updateFilteredNodesSingleFilter(filter);
    }
  };

  const activateFilterGroup = ({ filterId }: { filterId: number }) => {
    const activeFilter = filters.find(f => Number(f.id) === filterId);
    if (!activeFilter?.nodes) {
      return;
    }

    const optionColorMap = new Map<number, string>();
    const optionSizeMap = new Map<number, number>();

    activeFilter.options.forEach(option => {
      optionColorMap.set(Number(option.id), option.color);
      optionSizeMap.set(Number(option.id), option.size ?? DEFAULT_NODE_RADIUS);
    });

    setFilteredNodes(prevNodes => {
      return prevNodes.map(node => {
        const existingNode = prevNodes.find(n => n.id === node.id);
        const filteredNode = activeFilter.nodes?.find(n => n.nodeId === node.id);
        const isMultiple = activeFilter.selection === 'multiple';

        if (isMultiple && isMultiFilterEnabled) {
          const filteredFilterNodes = activeFilter.nodes?.filter(n => n.nodeId === node.id);
          if (filteredFilterNodes?.length) {
            return {
              ...node,
              shape: activeFilter.shape || existingNode?.shape,
              color: theme.palette.brand.editorTabPrimary,
              size: node.size,
            };
          }
        } else if (filteredNode) {
          return {
            ...node,
            shape: activeFilter.shape || existingNode?.shape,
            color: optionColorMap.get(Number(filteredNode.optionId)) || '',
            size: optionSizeMap.get(Number(filteredNode.optionId)) ?? node.size,
          };
        }

        return node;
      });
    });

    if (!isMultiFilterEnabled) {
      // In single filter mode, replace all active filters with just this one
      setActiveFilterIds([filterId]);
    } else {
      // In multi-filter mode, add to existing filters
      setActiveFilterIds(prev => {
        if (!prev.includes(filterId)) {
          return [...prev, filterId];
        }
        return prev;
      });
    }

    setOptionCheckedIds([]);
  };

  const getUpdatedNodeProperties = (
    node: FilteredNodes,
    remainingFilters: number[]
  ): FilteredNodes => {
    return remainingFilters.reduce((acc: FilteredNodes, id) => {
      const activeFilter = filters.find(f => Number(f.id) === id);
      if (!activeFilter?.nodes) return acc;

      const filteredNode = activeFilter.nodes.find(n => n.nodeId === node.id);
      if (!filteredNode) return acc;

      const optionColor =
        activeFilter.options?.find(opt => opt.id === filteredNode.optionId)?.color || '';
      const optionSize = activeFilter.options?.find(opt => opt.id === filteredNode.optionId)?.size;

      return {
        ...acc,
        shape: activeFilter.shape,
        color: optionColor,
        size: optionSize ?? acc.size,
      };
    }, node);
  };

  const deactivateFilterGroup = (filterId: number) => {
    setFilteredNodes(prevNodes => {
      const remainingFilters = activeFilterIds.filter(id => id !== filterId);

      return prevNodes.map(node =>
        remainingFilters.length === 0
          ? { ...node, shape: 'circle', color: '', size: undefined }
          : { ...node, ...getUpdatedNodeProperties(node, remainingFilters) }
      );
    });

    setOptionCheckedIds([]);
  };

  const filterTitleDisplay = getDisplayText(filter.title || '', CHARACTER_LIMITS.TITLE_DISPLAY);

  return (
    <SidebarFilterItem>
      <FilterItemHeader>
        {filterTitleDisplay.needsTooltip ? (
          <Tooltip title={filterTitleDisplay.tooltipText} placement="top">
            <FilterItemTitle>{filterTitleDisplay.displayText}</FilterItemTitle>
          </Tooltip>
        ) : (
          <FilterItemTitle>{filterTitleDisplay.displayText}</FilterItemTitle>
        )}
        {!isMultiFilterEnabled && (
          <CustomSwitch
            onChange={() => {
              if (activeFilterIds.includes(Number(filter.id))) {
                setActiveFilterIds(prevIds => prevIds.filter(id => id !== Number(filter.id)));

                if (filter.id !== undefined) {
                  deactivateFilterGroup(filter.id);
                }

                return;
              }

              activateFilterGroup({ filterId: Number(filter.id) });
            }}
            isChecked={activeFilterIds.includes(filter.id!)}
            value={filter.id!}
          />
        )}
      </FilterItemHeader>
      <Divider variant="middle" sx={{ borderColor: theme.palette.brand.backgroundDialog }} />
      <FilterWrapper>
        {options.map(option => {
          const optionTitleDisplay = getDisplayText(
            option.title || '',
            CHARACTER_LIMITS.TITLE_DISPLAY
          );

          return (
            <FilterOptionWrapper key={option.id} onClick={() => handleChecked(option)}>
              {getFilterIcon(
                option.color || theme.palette.brand.textSecondary,
                optionCheckedIds.includes(option.id ?? -1),
                filter.selection!
              )}
              {optionTitleDisplay.needsTooltip ? (
                <Tooltip title={optionTitleDisplay.tooltipText} placement="top">
                  <OptionTitle $isMarked={optionCheckedIds.includes(option.id ?? -1)}>
                    {optionTitleDisplay.displayText}
                  </OptionTitle>
                </Tooltip>
              ) : (
                <OptionTitle $isMarked={optionCheckedIds.includes(option.id ?? -1)}>
                  {optionTitleDisplay.displayText}
                </OptionTitle>
              )}
            </FilterOptionWrapper>
          );
        })}
      </FilterWrapper>
    </SidebarFilterItem>
  );
};

const SidebarFilterItem = styled.div`
  width: 100%;
`;

const FilterItemTitle = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  word-wrap: break-word;
  width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 20px;
`;

const FilterWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 0 1rem 1.5rem;
  flex-direction: column;
`;

const FilterOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
`;

const FilterItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem 0.5rem 1.5rem;
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};

  &.Mui-checked {
    color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
  }
`;

const StyledSVG = styled(SVG)<{
  $color: string;
  $fillColor: string;
  $isChecked: boolean;
  $isMultiFilter: boolean;
  $isSquare: boolean;
}>`
  width: 1.5rem;
  height: 1.5rem;
  overflow: visible;

  ${({ $color, $isChecked, $fillColor, $isMultiFilter }) =>
    css`
      ${!$isMultiFilter &&
      css`
        ${!$isChecked &&
        css`
          fill: ${$fillColor};
          & path {
            stroke: ${$fillColor};
            fill: ${$fillColor};
          }
        `}
        ${$isChecked &&
        css`
          & circle:nth-child(2),
          rect:nth-child(2) {
            stroke: ${$color};
            stroke-width: 2;
          }
          & path {
            stroke: ${$fillColor};
          }
        `}
      `}
      ${$isMultiFilter &&
      css`
        & > path {
          fill: ${$fillColor};
          stroke: ${$fillColor};
        }
        & circle:nth-child(3),
        rect:nth-child(3) {
          fill: ${$fillColor};
        }
      `}
    `}
`;

const OptionTitle = styled.div<{ $isMarked: boolean }>`
  font-size: 1.4rem;
  max-width: 190px;
  word-break: break-word;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.7rem;

  ${({ $isMarked }) =>
    $isMarked &&
    css`
      font-weight: bold;
    `}
`;

export { FilterSidebarItem };
